import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://indmobility.com';
    const contactUrl = `${siteUrl}/contact`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact IND Mobility',
        description: 'Get in touch with IND Mobility for product information, test drive requests, warranty claims, and more.',
        url: contactUrl,
        isPartOf: { '@type': 'WebSite', name: 'IND Mobility', url: siteUrl },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: contactUrl },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'IND Mobility',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/c396747a877dd1d0cb2c362594545533/02f73f35a156274691f368b3b9fff544.png',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          availableLanguage: 'English',
          url: contactUrl,
        },
      },
    ];

    const scriptTags = schemas.map((schema) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.text = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      scriptTags.forEach((el) => {
        if (document.head.contains(el)) document.head.removeChild(el);
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    setSubmitStatus('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d72ooaiaegmdqoca82gg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-black/80 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://static.readdy.ai/image/c396747a877dd1d0cb2c362594545533/02f73f35a156274691f368b3b9fff544.png"
              alt="IND Mobility"
              className="h-12 w-auto"
            />
          </Link>
          <Link to="/" className="flex items-center space-x-2 text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-teal-500/8 rounded-full blur-3xl pointer-events-none"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-none mb-6">
            CONTACT <span className="text-teal-400">US</span>
          </h1>
        </div>
      </div>

      {/* Main content */}
      <div className="px-6 pb-24">
        <div className="max-w-2xl mx-auto">

          {/* Form */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-8 md:p-10">
            <h2 className="text-2xl font-black mb-8 tracking-tight">SEND A MESSAGE</h2>

            {submitStatus === 'success' ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-500/10 border border-teal-500/30 rounded-full mx-auto mb-6">
                  <i className="ri-check-line text-teal-400 text-3xl"></i>
                </div>
                <h3 className="text-xl font-black mb-3">Message Sent!</h3>
                <p className="text-gray-400 mb-8">We&apos;ll get back to you within 24 hours.</p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="px-6 py-3 bg-teal-500 text-black font-bold rounded-lg hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} id="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold tracking-widest text-gray-500 mb-2 uppercase">First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="John"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-widest text-gray-500 mb-2 uppercase">Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Doe"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold tracking-widest text-gray-500 mb-2 uppercase">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@indmobility.com"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-teal-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold tracking-widest text-gray-500 mb-2 uppercase">Subject</label>
                  <select
                    name="subject"
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-teal-500 transition-colors cursor-pointer"
                  >
                    <option value="">Select a topic...</option>
                    <option value="Product Info">Product Info</option>
                    <option value="Test Drive">Test Drive Request</option>
                    <option value="Warranty">Warranty Claim</option>
                    <option value="Repairs">Repairs & Service</option>
                    <option value="Shipping">Shipping & Delivery</option>
                    <option value="Financing">Financing Options</option>
                    <option value="Returns">Returns & Exchanges</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold tracking-widest text-gray-500 mb-2 uppercase">
                    Message <span className="text-zinc-600 normal-case font-normal tracking-normal">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Tell us how we can help..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-teal-500 transition-colors resize-none"
                  />
                </div>

                {submitStatus === 'error' && (
                  <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={submitStatus === 'sending'}
                  className="w-full bg-teal-500 text-black font-black py-4 rounded-lg hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed tracking-wider text-sm"
                >
                  {submitStatus === 'sending' ? 'SENDING...' : 'SEND MESSAGE'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer strip */}
      <div className="border-t border-zinc-800 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">&copy; 2026 IND Mobility. All rights reserved.</p>
          <Link to="/" className="text-gray-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
